import { useDocumentTitle } from "../hooks/useDocumentTitle";

const HowItStarted = () => {
  useDocumentTitle("About");
  return (
    <section className="px-6 md:px-12 lg:px-20 py-20 text-light-text dark:text-text-primary">

      {/* CENTERED CONTAINER FOR BOTH TITLE + CONTENT */}
      <div className="max-w-3xl mx-auto">

        {/* PAGE TITLE */}
        <div className="mb-12">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint">
            About
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
            The path to <span className="text-accent">DevSecOps</span>
          </h1>
        </div>

        {/* CONTENT */}
        <div className="space-y-6 text-base md:text-lg text-light-text-secondary dark:text-text-secondary leading-relaxed">

          <p>
            I started like many engineers do - curious about how things work,
            but without a map. My early work was in general IT and infrastructure
            support. I fixed networks, managed servers, and learned that most
            "mysterious" problems have a root cause you can trace if you are
            patient enough.
          </p>

          <p>
            The turning point came when I saw the same security mistakes being
            made repeatedly. Misconfigured access controls. Hardcoded secrets.
            Containers running as root. It wasn't that teams didn't care - they
            didn't have the tooling or the time to do it right.
          </p>

          <p className="text-light-text dark:text-text-primary font-medium">
            That's when I started thinking about security as infrastructure, not
            an afterthought.
          </p>

          <p>
            I began systematically learning: Docker and Kubernetes for
            containerization, Terraform and Cloudflare for infrastructure, and
            then the security layer - zero-trust access with Teleport, TLS
            fundamentals, RBAC hardening, and supply chain scanning. I didn't just
            read about these; I built them. Every concept got tested in a real
            environment, debugged at 2 AM, and then documented.
          </p>

          <p>
            The Kubernetes certifications became a natural next step. CKAD first
            - because understanding how applications live inside a cluster is the
            foundation. Now CKA and CKS are in progress, with a structured 40-day
            study plan where every day's learning gets synthesized into my{" "}
            <a
              href="https://brain.rakeshkadam.me"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-muted transition-colors underline underline-offset-2 decoration-accent/30"
            >
              Digital Brain
            </a>
            .
          </p>

          <p>
            The AI piece entered organically. I started using Claude Code and
            LLM agents to accelerate infrastructure work - generating IaC,
            analyzing logs, and maintaining my knowledge base. Now I'm
            experimenting with agent-maintained artifacts, where AI handles
            structure and cross-references while I focus on synthesis and
            decision-making.
          </p>

          <p className="text-light-text dark:text-text-primary font-medium">
            Today, I lead DevSecOps initiatives that bridge infrastructure,
            security, and AI. I believe the best engineers don't just operate
            systems - they design them to be secure, observable, and
            self-improving from day one.
          </p>

          {/* Experience */}
          <div className="pt-4">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint mb-3">
              Experience
            </p>
            <div className="space-y-5">
              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <p className="text-light-text dark:text-text-primary font-semibold">
                    DevSecOps Lead
                  </p>
                  <p className="text-sm text-light-muted dark:text-text-muted">
                    TechAlchemy · Dec 2023 – Present
                  </p>
                </div>
                <ul className="list-disc list-inside text-sm text-light-muted dark:text-text-muted leading-relaxed space-y-1">
                  <li>
                    Own cloud architecture across AWS multi-account, multi-environment systems supporting high-traffic Web3 and FinTech workloads.
                  </li>
                  <li>
                    Build and optimise CI/CD platforms using GitHub Actions and Bitbucket Pipelines with OIDC, Nx monorepos, Docker pipelines, and ECS deployments.
                  </li>
                  <li>
                    Lead Terraform automation for ECS, ECR, RDS, API Gateway, CloudFront, WAF, Route53, and VPC networking.
                  </li>
                  <li>
                    Architect observability platforms using Prometheus, Grafana, Loki, Grafana Alloy, and CloudWatch.
                  </li>
                  <li>
                    Implement security best practices: IAM least privilege, AWS WAF, secrets management, and vulnerability scanning.
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <p className="text-light-text dark:text-text-primary font-semibold">
                    DevOps Engineer
                  </p>
                  <p className="text-sm text-light-muted dark:text-text-muted">
                    TechAlchemy · Jul 2022 – Dec 2023
                  </p>
                </div>
                <ul className="list-disc list-inside text-sm text-light-muted dark:text-text-muted leading-relaxed space-y-1">
                  <li>
                    Designed AWS infrastructure for blockchain and FinTech apps using ECS, EC2, RDS, API Gateway, CloudFront, Route53, and ECR.
                  </li>
                  <li>
                    Built CI/CD pipelines with automated testing, Docker builds, and PostgreSQL migration workflows.
                  </li>
                  <li>
                    Provisioned infrastructure using Terraform reusable modules across multiple environments.
                  </li>
                  <li>
                    Implemented RDS read replicas, ECR VPC endpoints, backup automation, and CloudFront optimisations.
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <p className="text-light-text dark:text-text-primary font-semibold">
                    Consumption Specialist · Technical Specialist
                  </p>
                  <p className="text-sm text-light-muted dark:text-text-muted">
                    Noventiq (Softline) · Oct 2020 – Jul 2022
                  </p>
                </div>
                <ul className="list-disc list-inside text-sm text-light-muted dark:text-text-muted leading-relaxed space-y-1">
                  <li>
                    Led cloud adoption and infrastructure optimisation for enterprise customers across Azure environments.
                  </li>
                  <li>
                    Provided technical guidance on cloud architecture, cost optimisation, workload onboarding, and operational best practices.
                  </li>
                  <li>
                    Supported infrastructure monitoring, capacity planning, and cloud operations for enterprise customer environments.
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <p className="text-light-text dark:text-text-primary font-semibold">
                    DevOps Engineer
                  </p>
                  <p className="text-sm text-light-muted dark:text-text-muted">
                    BotBakery · Jan 2020 – Aug 2020
                  </p>
                </div>
                <ul className="list-disc list-inside text-sm text-light-muted dark:text-text-muted leading-relaxed space-y-1">
                  <li>
                    Managed cloud-hosted infrastructure across AWS, GCP, Azure, and Linux hosting environments.
                  </li>
                  <li>
                    Supported deployment automation, Linux server administration, and production troubleshooting.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Education */}
          <div className="pt-4">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint mb-3">
              Education
            </p>
            <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
              <p className="text-light-text dark:text-text-primary font-semibold">
                B.Tech · Cloud Technology and Information Security
              </p>
              <p className="text-sm text-light-muted dark:text-text-muted mt-1">
                Ajeenkya DY Patil University
              </p>
            </div>
          </div>

          {/* Publications */}
          <div className="pt-4">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint mb-3">
              Publications
            </p>
            <div className="space-y-4">
              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <p className="text-sm font-semibold text-light-text dark:text-text-primary">
                  A Proposed Methodology for Data Security Distribution Using IoT Devices and Blockchain for Vehicle Insurance Companies
                </p>
                <p className="text-xs text-light-muted dark:text-text-muted mt-1">
                  IEEE Xplore · 2019
                </p>
                <p className="text-sm text-light-muted dark:text-text-muted mt-2 leading-relaxed">
                  Proposed a decentralized architecture combining IoT telemetry with blockchain smart contracts to secure and distribute vehicle insurance data. Published in IEEE Xplore.
                </p>
              </div>
              <div className="rounded-xl border border-light-border bg-light-surface p-5 dark:border-border-subtle dark:bg-surface-1">
                <p className="text-sm font-semibold text-light-text dark:text-text-primary">
                  A Comprehensive Study on Linux Forensics
                </p>
                <p className="text-xs text-light-muted dark:text-text-muted mt-1">
                  Linux, Ubuntu, Volatility Framework
                </p>
                <p className="text-sm text-light-muted dark:text-text-muted mt-2 leading-relaxed">
                  Analyzed forensic artifact extraction on Linux systems using the Volatility framework, covering memory analysis, file system traces, and incident reconstruction.
                </p>
              </div>
            </div>
          </div>

          <p>
            If you're working on something at that intersection - distributed
            systems, security, or AI-augmented operations - I'd love to hear
            about it.
          </p>

        </div>
      </div>
    </section>
  );
};

export default HowItStarted;
